import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import dayjs from 'dayjs';
import { NGXLogger } from 'ngx-logger';
import {
  combineLatest,
  filter,
  map,
  Observable,
  Subject,
  take,
  takeUntil,
} from 'rxjs';
import {
  Assignment,
  AssignmentType,
} from '../../../../models/assignment.school.model';
import { Grade } from '../../../../models/grade.school.model';
import { Group } from '../../../../models/group.model';
import {
  SchoolMember,
  SchoolMemberType,
} from '../../../../models/member.school.model';
import { MembershipStatus } from '../../../../models/membership.status';
import { SchoolRoles } from '../../../../models/roles.school.model';
import { UserModel } from '../../../../models/user.model';
import { IsExpiredPipe } from '../../../../pipes/is-expired.pipe';
import { MomentPipe } from '../../../../pipes/moment.pipe';
import { SchoolRolesService } from '../../../../services/school-roles.service';
import { UtilsService } from '../../../../services/utils.service';
import * as assignmentActions from '../../../../state/actions/assignment.school.actions';
import { AppState } from '../../../../state/appstate';
import * as assignmentSelector from '../../../../state/selectors/assignment.school.selector';
import * as groupSelector from '../../../../state/selectors/group.selector';
import * as memberSelector from '../../../../state/selectors/member.school.selector';
import { BtnChipComponent } from '../../../components/buttons/btn-chip/btn-chip.component';
import { BtnFabComponent } from '../../../components/buttons/btn-fab/btn-fab.component';
import { BtnMoreComponent } from '../../../components/buttons/btn-more/btn-more.component';

@Component({
  selector: 'app-list-assignments',
  standalone: true,
  imports: [
    AsyncPipe,
    NgTemplateOutlet,
    BtnMoreComponent,
    MomentPipe,
    RouterLink,
    IsExpiredPipe,
    BtnFabComponent,
    BtnMoreComponent,
    BtnChipComponent,
  ],
  templateUrl: './list-assignments.component.html',
  styleUrl: './list-assignments.component.css',
})
export class ListAssignmentsComponent {
  route: ActivatedRoute = inject(ActivatedRoute);
  router: Router = inject(Router);
  roles: SchoolRolesService = inject(SchoolRolesService);
  logger: NGXLogger = inject(NGXLogger);
  utilsService: UtilsService = inject(UtilsService);
  private dialog: MatDialog = inject(MatDialog);

  assignments$: Observable<Assignment[] | null | undefined> = new Observable();

  user: UserModel | null | undefined;
  loading$: Observable<boolean> = new Observable();
  inProgress$: Observable<boolean> = new Observable();
  isTeacher: boolean = false;

  groupId: string | null = this.route.root.snapshot.paramMap.get('id');
  facultyId: string | null = this.route.root.snapshot.paramMap.get('facultyId');
  gradeId: string | null = this.route.root.snapshot.paramMap.get('gradeId');
  studentId: string | null = this.route.root.snapshot.paramMap.get('studentId');
  academicYear: number | undefined = undefined;

  group: Group | null | undefined;
  faculty: SchoolMember | null | undefined;
  student: SchoolMember | null | undefined;
  grade: Grade | null | undefined;

  membershipStatus = MembershipStatus;
  memberType = SchoolMemberType;
  rolesType = SchoolRoles;

  myRoles$: Observable<any> = new Observable();

  exit$: Subject<void> = new Subject<void>();
  dayjs = dayjs;
  assignmentType = AssignmentType;

  private params$ = combineLatest({
    // Use .parent if the component is in a child router-outlet
    params: this.route.paramMap,
    // Use queryParams from the current route
    queryParams: this.route.queryParamMap,
  }).pipe(
    map(({ params, queryParams }) => {
      // If current params are empty, try to look at the parent
      let pMap = params;
      let currentRoute = this.route;

      // Walk up the tree until we find the groupId or hit the top
      while (currentRoute.parent && !pMap.has('id')) {
        currentRoute = currentRoute.parent;
        pMap = currentRoute.snapshot.paramMap;
      }

      return {
        groupId: pMap.get('id'),
        facultyId: pMap.get('facultyId'),
        gradeId: pMap.get('gradeId'),
        studentId: pMap.get('studentId'),
        academicYear: queryParams.get('academicYear')
          ? Number(queryParams.get('academicYear'))
          : undefined,
      };
    }),
    takeUntil(this.exit$),
  );

  constructor(private store: Store<AppState>) {
    this.loading$ = this.store.select(assignmentSelector.loading);

    this.inProgress$ = this.store
      .select(assignmentSelector.inProgress)
      .pipe(takeUntil(this.exit$));

    this.store.select(groupSelector.getSelectedGroup).subscribe((group) => {
      this.group = group;
    });
  }

  ngOnInit() {
    this.params$.subscribe(
      ({ groupId, facultyId, studentId, gradeId, academicYear }) => {
        this.groupId = groupId;
        this.facultyId = facultyId;
        this.gradeId = gradeId;
        this.studentId = studentId;
        this.academicYear = academicYear;

        this.logger.debug(
          `ListSessionsComponent :: Received route params groupId=${groupId}, facultyId=${facultyId}, studentId=${studentId}, gradeId=${gradeId}, academicYear=${academicYear}`,
        );

        if (groupId) {
          this.loadData(groupId, facultyId, studentId, gradeId, academicYear);
        }

        this.myRoles$ = this.store
          .select(groupSelector.getMyRolesInGroup(this.groupId))
          .pipe(takeUntil(this.exit$));
      },
    );

    this.isTeacher = this.router.url.includes('/faculty');
  }

  loadData(
    groupId: string,
    facultyId: string | null,
    studentId: string | null,
    gradeId: string | null,
    academicYear?: number,
  ) {
    this.store
      .select(groupSelector.getGroupById(groupId))
      .pipe(take(1))
      .subscribe((group) => {
        this.group = group;
        const effectiveYear = academicYear ?? group?.academicYear;
        this.academicYear = effectiveYear;

        this.logger.debug(
          `ListAssignmentsComponent :: Loaded group ${group?.name} with academicYear=${group?.academicYear}. Effective academic year for assignments: ${effectiveYear}`,
        );

        if (effectiveYear) {
          this.initDataStreams(
            groupId,
            facultyId,
            studentId,
            gradeId,
            effectiveYear,
          );
        }
      });
  }

  initDataStreams(
    groupId: string,
    facultyId: string | null,
    studentId: string | null,
    gradeId: string | null,
    academicYear: number,
  ) {
    this.logger.debug(
      `ListAssignmentsComponent :: Initializing data streams with groupId=${groupId}, facultyId=${facultyId}, gradeId=${gradeId}, academicYear=${academicYear}`,
    );

    if (gradeId) {
      this.setupGradeAndAssignments(groupId, gradeId, academicYear);
    } else if (facultyId) {
      this.store
        .select(memberSelector.getSchoolMemberById(facultyId, groupId))
        .pipe(
          filter((m) => !!m),
          takeUntil(this.exit$),
        )
        .subscribe((teacher) => {
          this.faculty = teacher;
          this.logger.debug(
            `ListAssignmentsComponent :: Loaded teacher ${teacher?.name} with gradeId=${teacher?.gradeId}`,
          );
          if (teacher?.gradeId) {
            this.setupGradeAndAssignments(
              groupId,
              teacher.gradeId,
              academicYear,
            );
          }
        });
    } else if (studentId) {
      this.store
        .select(memberSelector.getSchoolMemberById(studentId, groupId))
        .pipe(
          filter((m) => !!m),
          takeUntil(this.exit$),
        )
        .subscribe((student) => {
          this.student = student;
          this.logger.debug(
            `ListAssignmentsComponent :: Loaded student ${student?.name} with gradeId=${student?.gradeId}`,
          );
          if (student?.gradeId) {
            this.setupGradeAndAssignments(
              groupId,
              student.gradeId,
              academicYear,
            );
          }
        });
    }
  }

  private setupGradeAndAssignments(
    groupId: string,
    gradeId: string,
    academicYear: number,
  ) {
    this.logger.debug(
      `ListAssignmentsComponent :: Setting up data streams for gradeId=${gradeId}, academicYear=${academicYear}`,
    );
    // Set up the assignments observable for the UI
    this.assignments$ = this.store.select(
      assignmentSelector.getAssignmentsByGrade({
        gradeId,
        groupId: this.groupId,
        academicYear,
      }),
    );

    this.assignments$?.pipe(take(1)).subscribe((items) => {
      if (!items || items.length === 0) {
        this.logger.debug(
          `ListAssignmentsComponent :: No assignments found for gradeId=${gradeId}, academicYear=${academicYear}. Dispatching getAssignments action.`,
        );
        this.store.dispatch(
          assignmentActions.getAssignments({
            groupId,
            gradeId,
            academicYear,
          }),
        );
      }
    });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  onEditAssignment(item: Assignment) {
    this.router.navigate([item.id, 'edit'], {
      relativeTo: this.route,
      queryParams: { gradeId: this.gradeId },
    });
  }

  onEditNotes(item: Assignment) {
    this.router.navigate([item.id, 'editnotes'], {
      relativeTo: this.route,
      queryParams: { gradeId: this.gradeId },
    });
  }

  addMore() {
    this.assignments$?.pipe(take(1)).subscribe((assignments) => {
      const items = assignments ?? [];
      const lastModified =
        items?.length > 0 ? items[items?.length - 1]?.modified : undefined;
      this.store.dispatch(
        assignmentActions.getAssignments({
          groupId: this.groupId!,
          gradeId: this.gradeId!,
          academicYear: this.academicYear!,
          lastRecord: lastModified,
        }),
      );
    });
  }

  onDelete(item: Assignment) {
    this.utilsService
      .onDeleteConfirmation({
        title: 'Confirm Delete',
        description: `Are you sure you want to delete the assignment "${item.lessonName}"? This action cannot be undone.`,
      })
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (confirmed) {
          return this.store.dispatch(
            assignmentActions.deleteAction({
              groupId: item.groupId!,
              assignmentId: item?.id!,
            }),
          );
        }
        return;
      });
  }
}
